import React from 'react';
import {
    HelpCircle,
    Info,
    AlertTriangle,
    CheckCircle,
    Lightbulb,
    ArrowRight,
    ChevronRight,
    X,
    Sparkles,
    BookOpen,
    Eye,
    Target
} from 'lucide-react';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from './ui/tooltip';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

/**
 * ===============================================
 * YULONTAP USABILITY COMPONENTS
 * "Zero-Training Design" Component Library
 * ===============================================
 * 
 * These components help make the portal completely
 * intuitive for new users without any training.
 */

// ============================================
// HELP & TOOLTIPS
// ============================================

/**
 * HelpTooltip - A reusable help tooltip component
 * Use next to labels or titles that need explanation
 * 
 * @example
 * <Label>Baseline Date <HelpTooltip text="The starting date for tracking..." /></Label>
 */
export const HelpTooltip = ({ text, position = "top", className = "" }) => (
    <TooltipProvider>
        <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
                <HelpCircle 
                    className={`w-4 h-4 text-muted-foreground hover:text-muted-foreground cursor-help inline-block ml-1 ${className}`}
                    aria-label="Help"
                />
            </TooltipTrigger>
            <TooltipContent side={position} className="max-w-xs z-[100]">
                <p className="text-sm">{text}</p>
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
);

/**
 * FieldLabel - Label with optional help tooltip and required indicator
 * 
 * @example
 * <FieldLabel 
 *   label="Location Name" 
 *   required 
 *   helpText="Enter a unique name for this location"
 * />
 */
export const FieldLabel = ({ label, required = false, helpText, htmlFor, className = "" }) => (
    <label 
        htmlFor={htmlFor}
        className={`block text-sm font-medium text-foreground mb-1.5 ${className}`}
    >
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
        {!required && <span className="text-muted-foreground text-xs ml-1">(optional)</span>}
        {helpText && <HelpTooltip text={helpText} />}
    </label>
);

// ============================================
// BANNERS & ALERTS
// ============================================

/**
 * InfoBanner - Contextual information banners
 * Use to explain complex features or provide tips
 * 
 * @example
 * <InfoBanner type="tip" title="Pro Tip">
 *   Set baseline values to track ROI accurately
 * </InfoBanner>
 */
export const InfoBanner = ({ type = "info", title, children, onDismiss, className = "" }) => {
    const styles = {
        info: "bg-blue-50 border-blue-200 text-blue-800",
        warning: "bg-amber-50 border-amber-200 text-amber-800",
        success: "bg-emerald-50 border-emerald-200 text-emerald-800",
        tip: "bg-purple-50 border-purple-200 text-purple-800",
        error: "bg-red-50 border-red-200 text-red-800"
    };
    
    const icons = {
        info: <Info className="w-5 h-5 text-blue-500 flex-shrink-0" />,
        warning: <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />,
        success: <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />,
        tip: <Lightbulb className="w-5 h-5 text-purple-500 flex-shrink-0" />,
        error: <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
    };
    
    return (
        <div className={`rounded-lg border px-4 py-3 ${styles[type]} ${className}`}>
            <div className="flex items-start gap-3">
                {icons[type]}
                <div className="flex-1 min-w-0">
                    {title && <span className="font-semibold block mb-0.5">{title}</span>}
                    <div className="text-sm">{children}</div>
                </div>
                {onDismiss && (
                    <button onClick={onDismiss} className="text-current opacity-50 hover:opacity-100">
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
};

/**
 * ViewOnlyBanner - Shows when user has restricted access
 * 
 * @example
 * {isViewOnly() && <ViewOnlyBanner role="Franchise Admin" />}
 */
export const ViewOnlyBanner = ({ role = "View Only", message }) => (
    <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3 flex items-center gap-3">
        <Eye className="w-5 h-5 text-indigo-500" />
        <div className="flex-1">
            <Badge className="bg-indigo-100 text-indigo-700 mr-2">{role}</Badge>
            <span className="text-sm text-indigo-700">
                {message || "You have view-only access to this page. Editing is restricted."}
            </span>
        </div>
    </div>
);

/**
 * FeatureIntro - Introduction banner for new/complex features
 * 
 * @example
 * <FeatureIntro
 *   title="What are Baseline Values?"
 *   description="Baseline values are your starting metrics..."
 *   learnMoreUrl="/docs/baselines"
 * />
 */
export const FeatureIntro = ({ title, description, learnMoreUrl, onDismiss }) => (
    <div className="bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200 rounded-xl p-5">
        <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-1">{title}</h3>
                <p className="text-muted-foreground text-sm mb-3">{description}</p>
                {learnMoreUrl && (
                    <a 
                        href={learnMoreUrl} 
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1"
                    >
                        Learn more <ChevronRight className="w-4 h-4" />
                    </a>
                )}
            </div>
            {onDismiss && (
                <button onClick={onDismiss} className="text-muted-foreground hover:text-muted-foreground">
                    <X className="w-5 h-5" />
                </button>
            )}
        </div>
    </div>
);

// ============================================
// EMPTY STATES
// ============================================

/**
 * EmptyState - Consistent empty state with action
 * Use when a list/table has no data
 * 
 * @example
 * <EmptyState 
 *   icon={MapPin}
 *   title="No locations yet"
 *   description="Add your first location to get started"
 *   action={<Button onClick={...}>Add Location</Button>}
 * />
 */
export const EmptyState = ({ icon: Icon, title, description, action, tip }) => (
    <div className="py-12 text-center">
        {Icon && (
            <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 dark:bg-slate-700 rounded-2xl flex items-center justify-center">
                <Icon className="w-8 h-8 text-muted-foreground" />
            </div>
        )}
        <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
        {description && <p className="text-muted-foreground mb-4 max-w-md mx-auto">{description}</p>}
        {action && <div className="mt-4">{action}</div>}
        {tip && (
            <p className="text-xs text-muted-foreground mt-4 flex items-center justify-center gap-1">
                <Lightbulb className="w-3 h-3" /> {tip}
            </p>
        )}
    </div>
);

/**
 * GettingStartedCard - Show next steps for new users
 * 
 * @example
 * <GettingStartedCard
 *   steps={[
 *     { title: "Add Organization", complete: true },
 *     { title: "Add Location", complete: false, current: true },
 *     { title: "Add Employees", complete: false },
 *   ]}
 * />
 */
export const GettingStartedCard = ({ steps, title = "Getting Started", onDismiss }) => (
    <div className="bg-gradient-to-br from-emerald-50 to-blue-50 border border-emerald-200 rounded-xl p-5">
        <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-emerald-500" />
                <h3 className="font-semibold text-foreground">{title}</h3>
            </div>
            {onDismiss && (
                <button onClick={onDismiss} className="text-muted-foreground hover:text-muted-foreground">
                    <X className="w-4 h-4" />
                </button>
            )}
        </div>
        <div className="space-y-3">
            {steps.map((step, idx) => (
                <div 
                    key={idx}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                        step.current ? 'bg-background shadow-sm border border-emerald-200' : 
                        step.complete ? 'opacity-60' : ''
                    }`}
                >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                        step.complete ? 'bg-emerald-500 text-white' :
                        step.current ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-500' :
                        'bg-slate-200 dark:bg-slate-600 text-muted-foreground'
                    }`}>
                        {step.complete ? <CheckCircle className="w-4 h-4" /> : idx + 1}
                    </div>
                    <div className="flex-1">
                        <p className={`font-medium ${step.complete ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                            {step.title}
                        </p>
                        {step.description && (
                            <p className="text-sm text-muted-foreground">{step.description}</p>
                        )}
                    </div>
                    {step.current && step.action && (
                        <Button size="sm" onClick={step.action.onClick}>
                            {step.action.label} <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                    )}
                </div>
            ))}
        </div>
    </div>
);

// ============================================
// PAGE STRUCTURE
// ============================================

/**
 * PageHeader - Consistent page header with title, description, and actions
 * 
 * @example
 * <PageHeader 
 *   title="Locations"
 *   description="Manage your restaurant locations"
 *   actions={<Button>Add Location</Button>}
 * />
 */
export const PageHeader = ({ 
    title, 
    description, 
    actions, 
    badge,
    helpText,
    breadcrumbs
}) => (
    <div className="space-y-2">
        {breadcrumbs && (
            <nav className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
                {breadcrumbs.map((crumb, idx) => (
                    <React.Fragment key={idx}>
                        {idx > 0 && <ChevronRight className="w-4 h-4" />}
                        {crumb.href ? (
                            <a href={crumb.href} className="hover:text-foreground">{crumb.label}</a>
                        ) : (
                            <span className="text-foreground font-medium">{crumb.label}</span>
                        )}
                    </React.Fragment>
                ))}
            </nav>
        )}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
                <div className="flex items-center gap-3">
                    <h1 
                        className="text-2xl sm:text-3xl font-bold text-foreground" 
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                    >
                        {title}
                    </h1>
                    {badge}
                    {helpText && <HelpTooltip text={helpText} />}
                </div>
                {description && <p className="text-muted-foreground mt-1">{description}</p>}
            </div>
            {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
    </div>
);

/**
 * SectionHeader - Header for card/section content
 * 
 * @example
 * <SectionHeader 
 *   title="Platform Settings"
 *   subtitle="Configure review platform URLs"
 * />
 */
export const SectionHeader = ({ title, subtitle, icon: Icon, helpText, actions }) => (
    <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
            {Icon && (
                <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-muted-foreground" />
                </div>
            )}
            <div>
                <h3 className="font-semibold text-foreground flex items-center gap-2">
                    {title}
                    {helpText && <HelpTooltip text={helpText} />}
                </h3>
                {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
            </div>
        </div>
        {actions}
    </div>
);

// ============================================
// METRIC CARDS
// ============================================

/**
 * MetricCard - Display a key metric with label and optional trend
 * 
 * @example
 * <MetricCard 
 *   label="Total Reviews"
 *   value="1,234"
 *   sublabel="Since baseline date"
 *   trend="+12%"
 *   trendUp={true}
 * />
 */
export const MetricCard = ({ 
    label, 
    value, 
    sublabel, 
    icon: Icon, 
    trend, 
    trendUp,
    helpText,
    color = "blue" 
}) => {
    const colorStyles = {
        blue: "bg-blue-100 text-blue-600",
        green: "bg-emerald-100 text-emerald-600",
        purple: "bg-purple-100 text-purple-600",
        amber: "bg-amber-100 text-amber-600",
        red: "bg-red-100 text-red-600"
    };
    
    return (
        <div className="bg-background rounded-xl border p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <p className="text-sm text-muted-foreground mb-1 flex items-center">
                        {label}
                        {helpText && <HelpTooltip text={helpText} position="right" />}
                    </p>
                    <p className="text-3xl font-bold text-foreground">{value}</p>
                    {sublabel && (
                        <p className="text-sm text-muted-foreground mt-1">{sublabel}</p>
                    )}
                    {trend && (
                        <p className={`text-sm mt-2 font-medium ${trendUp ? 'text-emerald-600' : 'text-red-600'}`}>
                            {trend}
                        </p>
                    )}
                </div>
                {Icon && (
                    <div className={`w-10 h-10 rounded-xl ${colorStyles[color]} flex items-center justify-center`}>
                        <Icon className="w-5 h-5" />
                    </div>
                )}
            </div>
        </div>
    );
};

// ============================================
// FORM HELPERS
// ============================================

/**
 * FormSection - Group related form fields together
 * 
 * @example
 * <FormSection title="Contact Information" description="Enter primary contact details">
 *   <Input ... />
 *   <Input ... />
 * </FormSection>
 */
export const FormSection = ({ title, description, children, collapsible = false }) => {
    const [isOpen, setIsOpen] = React.useState(true);
    
    return (
        <div className="border-b border-slate-200 pb-6 mb-6 last:border-b-0 last:pb-0 last:mb-0">
            <div 
                className={`mb-4 ${collapsible ? 'cursor-pointer' : ''}`}
                onClick={() => collapsible && setIsOpen(!isOpen)}
            >
                <h4 className="font-medium text-foreground flex items-center gap-2">
                    {title}
                    {collapsible && (
                        <ChevronRight className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                    )}
                </h4>
                {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
            </div>
            {(!collapsible || isOpen) && (
                <div className="space-y-4">
                    {children}
                </div>
            )}
        </div>
    );
};

/**
 * RequiredFieldsNote - Note about required fields in a form
 */
export const RequiredFieldsNote = () => (
    <p className="text-xs text-muted-foreground mb-4">
        Fields marked with <span className="text-red-500">*</span> are required
    </p>
);

// ============================================
// QUICK ACTIONS
// ============================================

/**
 * QuickAction - A quick action button with icon
 * 
 * @example
 * <QuickAction icon={Plus} label="Add New" onClick={...} />
 */
export const QuickAction = ({ icon: Icon, label, onClick, variant = "default", disabled = false }) => {
    const variants = {
        default: "bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:bg-slate-600 text-foreground",
        primary: "bg-blue-100 hover:bg-blue-200 text-blue-700",
        success: "bg-emerald-100 hover:bg-emerald-200 text-emerald-700",
        warning: "bg-amber-100 hover:bg-amber-200 text-amber-700",
        danger: "bg-red-100 hover:bg-red-200 text-red-700"
    };
    
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]}`}
        >
            {Icon && <Icon className="w-4 h-4" />}
            {label}
        </button>
    );
};

// ============================================
// STATUS INDICATORS
// ============================================

/**
 * StatusIndicator - Visual status with label
 * 
 * @example
 * <StatusIndicator status="healthy" />
 * <StatusIndicator status="warning" label="Needs Attention" />
 */
export const StatusIndicator = ({ status, label, size = "default" }) => {
    const statusConfig = {
        healthy: { color: "bg-emerald-500", text: "Healthy", bgLight: "bg-emerald-50", textColor: "text-emerald-700" },
        warning: { color: "bg-amber-500", text: "Needs Attention", bgLight: "bg-amber-50", textColor: "text-amber-700" },
        critical: { color: "bg-red-500", text: "Critical", bgLight: "bg-red-50", textColor: "text-red-700" },
        active: { color: "bg-emerald-500", text: "Active", bgLight: "bg-emerald-50", textColor: "text-emerald-700" },
        inactive: { color: "bg-slate-400", text: "Inactive", bgLight: "bg-muted", textColor: "text-muted-foreground" },
        pending: { color: "bg-blue-500", text: "Pending", bgLight: "bg-blue-50", textColor: "text-blue-700" }
    };
    
    const config = statusConfig[status] || statusConfig.inactive;
    const displayLabel = label || config.text;
    
    const sizeClasses = {
        small: "h-2 w-2",
        default: "h-2.5 w-2.5",
        large: "h-3 w-3"
    };
    
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${config.bgLight} ${config.textColor}`}>
            <span className={`${sizeClasses[size]} rounded-full ${config.color}`} />
            <span className="text-xs font-medium">{displayLabel}</span>
        </span>
    );
};

// ============================================
// ONBOARDING CHECKLIST
// ============================================

/**
 * OnboardingChecklist - Track user's onboarding progress
 * 
 * @example
 * <OnboardingChecklist
 *   items={[
 *     { id: 'org', label: 'Add Organization', complete: true },
 *     { id: 'loc', label: 'Add Location', complete: false },
 *   ]}
 * />
 */
export const OnboardingChecklist = ({ items, title = "Setup Checklist", onDismiss }) => {
    const completedCount = items.filter(i => i.complete).length;
    const progress = (completedCount / items.length) * 100;
    
    return (
        <div className="bg-background border rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                        <Target className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-foreground">{title}</h3>
                        <p className="text-sm text-muted-foreground">{completedCount} of {items.length} complete</p>
                    </div>
                </div>
                {onDismiss && (
                    <button onClick={onDismiss} className="text-muted-foreground hover:text-muted-foreground">
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>
            
            {/* Progress bar */}
            <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full mb-4 overflow-hidden">
                <div 
                    className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                />
            </div>
            
            {/* Checklist items */}
            <div className="space-y-2">
                {items.map(item => (
                    <div 
                        key={item.id}
                        className={`flex items-center gap-3 p-2 rounded-lg ${item.complete ? 'bg-muted' : 'hover:bg-muted'}`}
                    >
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                            item.complete ? 'bg-emerald-500' : 'border-2 border-slate-300'
                        }`}>
                            {item.complete && <CheckCircle className="w-3 h-3 text-white" />}
                        </div>
                        <span className={`flex-1 text-sm ${item.complete ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                            {item.label}
                        </span>
                        {item.action && !item.complete && (
                            <Button size="sm" variant="ghost" onClick={item.action.onClick}>
                                {item.action.label}
                            </Button>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

// ============================================
// DEFINITION LIST (for displaying key-value pairs)
// ============================================

/**
 * DefinitionList - Display key-value pairs clearly
 * 
 * @example
 * <DefinitionList
 *   items={[
 *     { term: "Google Reviews", definition: "The total count of Google reviews" },
 *     { term: "Baseline Date", definition: "When tracking started" }
 *   ]}
 * />
 */
export const DefinitionList = ({ items, columns = 1 }) => (
    <dl className={`grid gap-4 ${columns === 2 ? 'md:grid-cols-2' : ''}`}>
        {items.map((item, idx) => (
            <div key={idx} className="bg-muted rounded-lg p-3">
                <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                    {item.term}
                    {item.helpText && <HelpTooltip text={item.helpText} />}
                </dt>
                <dd className="text-sm text-foreground font-medium">{item.definition || '—'}</dd>
            </div>
        ))}
    </dl>
);

// ============================================
// EXPORTS
// ============================================

export default {
    // Help & Tooltips
    HelpTooltip,
    FieldLabel,
    
    // Banners & Alerts
    InfoBanner,
    ViewOnlyBanner,
    FeatureIntro,
    
    // Empty States
    EmptyState,
    GettingStartedCard,
    
    // Page Structure
    PageHeader,
    SectionHeader,
    
    // Metrics
    MetricCard,
    
    // Forms
    FormSection,
    RequiredFieldsNote,
    
    // Actions
    QuickAction,
    
    // Status
    StatusIndicator,
    
    // Onboarding
    OnboardingChecklist,
    
    // Lists
    DefinitionList
};
